import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { emailingAPI } from '@/lib/api';
import { formatDate } from '@/lib/utils';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  Plus,
  Settings,
  Clock,
  PlayCircle,
  Pause,
  Trash2,
  Pencil,
  Loader2,
  Calendar,
  Zap,
  CheckCircle,
  XCircle,
  RefreshCw,
  ArrowLeft,
  Mail,
} from 'lucide-react';

const AutomationRulesPage = () => {
  const navigate = useNavigate();
  const [rules, setRules] = useState([]);
  const [campaigns, setCampaigns] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  
  // Create/Edit dialog
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formLoading, setFormLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    campaign_id: '',
    enabled: true,
    recurrence_type: 'weekly',
    recurrence_config: {
      day_of_week: 'mon',
      hour: 9,
      minute: 0
    },
    trigger_condition: 'none',
    trigger_params: {}
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [rulesRes, campaignsRes, configRes] = await Promise.all([
        emailingAPI.getAutomationRules(),
        emailingAPI.getCampaigns(),
        emailingAPI.getAutomationConfig()
      ]);
      
      setRules(rulesRes.data.rules);
      setCampaigns(campaignsRes.data.campaigns);
      setConfig(configRes.data);
    } catch (error) {
      toast.error('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingRule(null);
    setForm({
      name: '',
      campaign_id: campaigns[0]?.id || '',
      enabled: true,
      recurrence_type: 'weekly',
      recurrence_config: {
        day_of_week: 'mon',
        hour: 9,
        minute: 0
      },
      trigger_condition: 'none',
      trigger_params: {}
    });
    setDialogOpen(true);
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      name: rule.name,
      campaign_id: rule.campaign_id,
      enabled: rule.enabled,
      recurrence_type: rule.recurrence_type,
      recurrence_config: rule.recurrence_config || {},
      trigger_condition: rule.trigger_condition || 'none',
      trigger_params: rule.trigger_params || {}
    });
    setDialogOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    
    if (!form.name || !form.campaign_id) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    
    setFormLoading(true);
    try {
      if (editingRule) {
        await emailingAPI.updateAutomationRule(editingRule.id, form);
        toast.success('Règle mise à jour');
      } else {
        await emailingAPI.createAutomationRule(form);
        toast.success('Règle créée');
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error('Erreur lors de l\'enregistrement');
    } finally {
      setFormLoading(false);
    }
  };

  const handleToggle = async (ruleId, enabled) => {
    try {
      await emailingAPI.toggleAutomationRule(ruleId, enabled);
      toast.success(enabled ? 'Règle activée' : 'Règle désactivée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la modification');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Supprimer cette règle ?')) return;
    
    try {
      await emailingAPI.deleteAutomationRule(ruleId);
      toast.success('Règle supprimée');
      loadData();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleExecuteNow = async (ruleId) => {
    try {
      toast.info('Exécution en cours...');
      const response = await emailingAPI.executeAutomationRule(ruleId);
      if (response.data.success) {
        toast.success(`${response.data.last_sent_count} email(s) envoyé(s)`);
        loadData();
      } else {
        toast.error('Erreur lors de l\'exécution');
      }
    } catch (error) {
      toast.error('Erreur lors de l\'exécution');
    }
  };

  const getCampaignName = (campaignId) => {
    const campaign = campaigns.find(c => c.id === campaignId);
    return campaign?.name || 'Campagne inconnue';
  };

  const getRecurrenceLabel = (rule) => {
    const config = rule.recurrence_config || {};
    switch (rule.recurrence_type) {
      case 'daily':
        return `Tous les jours à ${config.hour || 9}h${String(config.minute || 0).padStart(2, '0')}`;
      case 'weekly':
        const dayLabel = config.weekdays?.[config.day_of_week] || config.day_of_week || 'lundi';
        return `Chaque ${dayLabel} à ${config.hour || 9}h${String(config.minute || 0).padStart(2, '0')}`;
      case 'monthly':
        return `Le ${config.day_of_month || 1} de chaque mois à ${config.hour || 9}h`;
      case 'custom':
        return `CRON: ${config.cron_expression || '0 9 * * 1'}`;
      default:
        return 'Non défini';
    }
  };

  const getConditionLabel = (rule) => {
    switch (rule.trigger_condition) {
      case 'devis_pending_days':
        return `Devis en attente ≥ ${rule.trigger_params?.days || 7} jours`;
      case 'client_inactive_days':
        return `Client inactif ≥ ${rule.trigger_params?.days || 90} jours`;
      case 'new_contact_delay':
        return `Nouveau contact (${rule.trigger_params?.hours || 24}h après)`;
      default:
        return 'Aucune condition';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="automation-rules-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-4">
          <Button variant="ghost" onClick={() => navigate('/admin/emailing')}>
            <ArrowLeft className="w-4 h-4 mr-2" />
            Retour
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-slate-800" style={{ fontFamily: 'Barlow Condensed' }}>
              <Zap className="w-6 h-6 inline mr-2 text-yellow-500" />
              Automatisations
            </h1>
            <p className="text-slate-500">{rules.length} règle(s) configurée(s)</p>
          </div>
        </div>
        
        <Button
          className="bg-orange-500 hover:bg-orange-600"
          onClick={handleCreate}
          data-testid="new-rule-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Nouvelle règle
        </Button>
      </div>

      {/* Info Card */}
      <Card className="border-blue-200 bg-blue-50/50">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-blue-100 rounded-lg">
              <Clock className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="font-medium text-slate-800">Envois automatiques</p>
              <p className="text-sm text-slate-600 mt-1">
                Les règles d'automatisation envoient automatiquement vos campagnes selon un planning défini.
                Par exemple : "Envoyer la relance devis tous les lundis à 9h aux clients avec des devis non validés depuis 7 jours."
              </p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Rules Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          {rules.length === 0 ? (
            <div className="p-8 text-center text-slate-500">
              <Zap className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>Aucune règle d'automatisation</p>
              <p className="text-sm mt-2">Créez votre première règle pour automatiser l'envoi de vos campagnes</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Statut</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Règle</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500">Campagne</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden md:table-cell">Récurrence</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden lg:table-cell">Condition</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 hidden xl:table-cell">Prochaine exécution</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-wider text-slate-500 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rules.map((rule) => (
                    <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                      <TableCell>
                        <Switch
                          checked={rule.enabled}
                          onCheckedChange={(checked) => handleToggle(rule.id, checked)}
                        />
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium text-slate-800">{rule.name}</p>
                          <p className="text-xs text-slate-500">
                            {rule.run_count} exécution(s) • {rule.last_sent_count || 0} dernier envoi
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          <Mail className="w-3 h-3 mr-1" />
                          {getCampaignName(rule.campaign_id)}
                        </Badge>
                      </TableCell>
                      <TableCell className="hidden md:table-cell">
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <Calendar className="w-4 h-4" />
                          {getRecurrenceLabel(rule)}
                        </div>
                      </TableCell>
                      <TableCell className="hidden lg:table-cell text-sm text-slate-600">
                        {getConditionLabel(rule)}
                      </TableCell>
                      <TableCell className="hidden xl:table-cell text-sm text-slate-500">
                        {rule.next_run ? formatDate(rule.next_run) : '-'}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-end gap-1">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-green-600"
                            onClick={() => handleExecuteNow(rule.id)}
                            title="Exécuter maintenant"
                          >
                            <PlayCircle className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8"
                            onClick={() => handleEdit(rule)}
                            title="Modifier"
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 text-red-500"
                            onClick={() => handleDelete(rule.id)}
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              <Zap className="w-5 h-5 inline mr-2 text-yellow-500" />
              {editingRule ? 'Modifier la règle' : 'Nouvelle règle d\'automatisation'}
            </DialogTitle>
            <DialogDescription>
              Configurez l'envoi automatique de vos campagnes
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSave} className="space-y-4 mt-4">
            {/* Nom */}
            <div className="space-y-2">
              <Label htmlFor="name">Nom de la règle *</Label>
              <Input
                id="name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Ex: Relance devis hebdomadaire"
                required
                data-testid="rule-name-input"
              />
            </div>
            
            {/* Campagne */}
            <div className="space-y-2">
              <Label>Campagne à envoyer *</Label>
              <Select
                value={form.campaign_id}
                onValueChange={(value) => setForm({ ...form, campaign_id: value })}
              >
                <SelectTrigger data-testid="rule-campaign-select">
                  <SelectValue placeholder="Sélectionner une campagne" />
                </SelectTrigger>
                <SelectContent>
                  {campaigns.map((c) => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Récurrence */}
            <div className="space-y-2">
              <Label>Fréquence d'envoi</Label>
              <Select
                value={form.recurrence_type}
                onValueChange={(value) => setForm({
                  ...form,
                  recurrence_type: value,
                  recurrence_config: value === 'daily' ? { hour: 9, minute: 0 }
                    : value === 'weekly' ? { day_of_week: 'mon', hour: 9, minute: 0 }
                    : value === 'monthly' ? { day_of_month: 1, hour: 9, minute: 0 }
                    : {}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="daily">📅 Quotidien</SelectItem>
                  <SelectItem value="weekly">📆 Hebdomadaire</SelectItem>
                  <SelectItem value="monthly">🗓️ Mensuel</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Config récurrence */}
            {form.recurrence_type === 'weekly' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Jour</Label>
                  <Select
                    value={form.recurrence_config.day_of_week || 'mon'}
                    onValueChange={(value) => setForm({
                      ...form,
                      recurrence_config: { ...form.recurrence_config, day_of_week: value }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="mon">Lundi</SelectItem>
                      <SelectItem value="tue">Mardi</SelectItem>
                      <SelectItem value="wed">Mercredi</SelectItem>
                      <SelectItem value="thu">Jeudi</SelectItem>
                      <SelectItem value="fri">Vendredi</SelectItem>
                      <SelectItem value="sat">Samedi</SelectItem>
                      <SelectItem value="sun">Dimanche</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Select
                    value={String(form.recurrence_config.hour || 9)}
                    onValueChange={(value) => setForm({
                      ...form,
                      recurrence_config: { ...form.recurrence_config, hour: parseInt(value) }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((h) => (
                        <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {form.recurrence_type === 'daily' && (
              <div className="space-y-2">
                <Label>Heure d'envoi</Label>
                <Select
                  value={String(form.recurrence_config.hour || 9)}
                  onValueChange={(value) => setForm({
                    ...form,
                    recurrence_config: { ...form.recurrence_config, hour: parseInt(value) }
                  })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((h) => (
                      <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {form.recurrence_type === 'monthly' && (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Jour du mois</Label>
                  <Select
                    value={String(form.recurrence_config.day_of_month || 1)}
                    onValueChange={(value) => setForm({
                      ...form,
                      recurrence_config: { ...form.recurrence_config, day_of_month: parseInt(value) }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[1, 5, 10, 15, 20, 25, 28].map((d) => (
                        <SelectItem key={d} value={String(d)}>Le {d}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Heure</Label>
                  <Select
                    value={String(form.recurrence_config.hour || 9)}
                    onValueChange={(value) => setForm({
                      ...form,
                      recurrence_config: { ...form.recurrence_config, hour: parseInt(value) }
                    })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {[7, 8, 9, 10, 11, 12, 13, 14, 15, 16, 17, 18].map((h) => (
                        <SelectItem key={h} value={String(h)}>{h}h00</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}
            
            {/* Condition de déclenchement */}
            <div className="space-y-2">
              <Label>Condition supplémentaire</Label>
              <Select
                value={form.trigger_condition}
                onValueChange={(value) => setForm({
                  ...form,
                  trigger_condition: value,
                  trigger_params: value === 'devis_pending_days' ? { days: 7 }
                    : value === 'client_inactive_days' ? { days: 90 }
                    : {}
                })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucune - utiliser le filtre de la campagne</SelectItem>
                  <SelectItem value="devis_pending_days">📋 Devis en attente depuis X jours</SelectItem>
                  <SelectItem value="client_inactive_days">💤 Client inactif depuis X jours</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {form.trigger_condition === 'devis_pending_days' && (
              <div className="space-y-2">
                <Label>Devis en attente depuis (jours)</Label>
                <Input
                  type="number"
                  value={form.trigger_params.days || 7}
                  onChange={(e) => setForm({
                    ...form,
                    trigger_params: { ...form.trigger_params, days: parseInt(e.target.value) }
                  })}
                  min={1}
                  className="w-32"
                />
              </div>
            )}
            
            {form.trigger_condition === 'client_inactive_days' && (
              <div className="space-y-2">
                <Label>Inactif depuis (jours)</Label>
                <Input
                  type="number"
                  value={form.trigger_params.days || 90}
                  onChange={(e) => setForm({
                    ...form,
                    trigger_params: { ...form.trigger_params, days: parseInt(e.target.value) }
                  })}
                  min={7}
                  className="w-32"
                />
              </div>
            )}
            
            {/* Activé */}
            <div className="flex items-center gap-3 pt-2">
              <Switch
                checked={form.enabled}
                onCheckedChange={(checked) => setForm({ ...form, enabled: checked })}
              />
              <Label>Activer la règle</Label>
            </div>
            
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button
                type="submit"
                className="bg-orange-500 hover:bg-orange-600"
                disabled={formLoading}
                data-testid="save-rule-btn"
              >
                {formLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-2" />
                )}
                {editingRule ? 'Mettre à jour' : 'Créer la règle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AutomationRulesPage;
